<template>
  <div
    ref="accordion"
    class="relative px-4 md:px-6"
    :class="[
      component.wrapper,
      component.border
    ]"
  >
    <!-- eslint-disable vue/no-v-html -->
    <input
      ref="input"
      v-model="state"
      type="checkbox"
      name="accordion"
      class="peer appearance-none absolute w-full cursor-pointer top-0 left-0"
    />

    <div
      class="flex cursor-pointer justify-between align-middle font-bold text-semantic-surface-on-surface items-center"
      :class="[
        component.title,
        component.spacing,
        labelClass
      ]"
      @click="state = !state"
    >
      <div class="w-full flex items-center gap-4">
        <slot name="title-icon">
          <V2IconsCheck
            v-if="hasLeftIcon"
            :fill="checkFillcolor"
          />
        </slot>

        <slot
          v-if="$slots.title || title"
          name="title"
        >
          <div
            class="text-base/[150%] md:text-lg/[150%]"
            v-html="title"
          />
        </slot>
      </div>

      <slot name="icon">
        <template v-if="layout === 'default'">
          <V2IconsPlus
            v-if="!state"
            :fill="iconFillColor"
            class="ml-4"
          />

          <V2IconsMinus
            v-else
            :fill="iconFillColor"
            class="ml-4"
          />
        </template>

        <template v-if="layout === 'contained'">
          <V2IconsCaret
            :fill="iconFillColor"
            :direction="state ? 'top' : 'bottom'"
          />
        </template>
      </slot>
    </div>

    <div
      class="transition-all overflow-hidden max-h-0 z-10 peer-checked:max-h-max text-semantic-surface-on-surface text-sm/[150%] md:text-base/[150%]"
      :class="component.content"
      @click.stop
    >
      <slot>
        <div v-html="content" />
      </slot>
    </div>
  </div>
</template>

<script setup>
defineOptions({
  name: 'V2MoleculesAccordion'
})

const props = defineProps({
  layout: {
    type: String,
    default: 'default',
    validator: value => ['default', 'contained'].includes(value)
  },

  title: {
    type: String,
    default: ''
  },

  content: {
    type: String,
    default: ''
  },

  isFirstIndex: {
    type: Boolean,
    default: false
  },

  isLastIndex: {
    type: Boolean,
    default: false
  },

  hideInitialBorder: {
    type: Boolean,
    default: false
  },

  hideLastBorder: {
    type: Boolean,
    default: false
  },

  hasLeftIcon: {
    type: Boolean,
    default: false
  },

  size: {
    type: String,
    default: 'md',
    validator: value => ['xs', 'sm', 'md', 'lg'].includes(value)
  },

  iconFillColor: {
    type: String,
    default: () => semanticColorLookup('surface.on-surface')
  },

  labelClass: {
    type: String,
    default: ''
  }
})

const state = defineModel('state', {
  type: Boolean,
  default: false
})

const checkFillcolor = computed(() => {
  return semanticColorLookup('states.success.default')
})

const component = computed(() => {
  const wrapper = getKey(props.layout, {
    default: 'bg-transparent',
    contained: 'bg-semantic-surface-default'
  })

  let border = getKey(props.layout, {
    default: 'border-t-1 border-semantic-outline-variant last-of-type:border-b-1',
    contained: !state.value ? 'shadow border last-of-type:border-b-1 rounded-2xl': 'border last-of-type:border-b-1 rounded-2xl border-semantic-primary-variant-light'
  })

  if (props.hideInitialBorder && props.isFirstIndex) {
    border = border.replace('border-t-1', '')
  }

  if (props.hideLastBorder && props.isLastIndex) {
    border = border.replace('last-of-type:border-b-1', '')
  }

  const title = getKey(props.layout, {
    default: state.value ? '!text-semantic-primary-variant-light' : '',
    contained: ''
  })

  const content = getKey(props.layout, {
    default: 'peer-checked:pb-4 sm:peer-checked:pb-6',
    contained: 'peer-checked:pb-4'
  })

  const spacing = getKey(props.size, {
    xs: 'py-2',
    sm: 'py-3 md:py-4',
    md: 'py-4 md:py-6',
    lg: 'py-5 md:py-8'
  })

  return {
    wrapper,
    border,
    title: removeClassExtras(title),
    content,
    spacing
  }
})
</script>

<style lang="css" scoped>
.shadow {
  box-shadow: 0px 1px 2px 0px rgba(62, 25, 104, 0.20), 0px 1px 4px 0px rgba(62, 25, 104, 0.10);
}
</style>